import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Button, DialogActions, DialogContent, Stack, Tab, Tabs, Typography } from '@mui/material'
import { selectActiveChartId } from '../../../selectors/chart'
import { AppContext } from '../../../App'
import ChartDrawingSettingsGroup from './chartDrawingSettingsGroup/chartDrawingSettingsGroup'
import { DrawingSettingGroup } from '../drawingTools/drawing.interface'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import DraggableDialogTitle from '../../common/DraggableDialogTitle'
import CustomDialog from '../../common/CustomDialog'
import { ExtraSmallButton } from '../../custom'
import ClearIcon from '../../../assets/icons/ClearIcon'


export type ChartDrawingSettingsModalProps = {
    id: string,
    extraSettingsGroups?: DrawingSettingGroup<any, any>[],
    onDrawingSettingGroupsUpdated?: (sgs: DrawingSettingGroup<any, any>[]) => void,
    onClose: () => void
}


const ChartDrawingSettingsModal: React.FC<ChartDrawingSettingsModalProps> = ({
                                                                                 id,
                                                                                 onClose,
                                                                                 extraSettingsGroups: esgs,
                                                                                 onDrawingSettingGroupsUpdated
                                                                             }) => {

    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)

    const [extraSettingGroups, setExtraSettingGroups] = useState<DrawingSettingGroup<any, any>[]>((esgs ?? []))
    const initialExtraSettingGroups = useRef<DrawingSettingGroup<any, any>[]>(JSON.parse(JSON.stringify(esgs ?? [])))
    const settingsGroups = [
        ...extraSettingGroups,
        ...appContext.getChart(activeChartId).getDrawingSettings(id)
    ]
    const extraSettingGroupIds = useMemo(() => (extraSettingGroups ?? []).map((esg) => esg.id), [extraSettingGroups])
    const chart = appContext.getChart(activeChartId)
    const [selectedTab, setSelectedTab] = useState(() => settingsGroups.find((g) => g.visible)?.id ?? '')
    const container = useSelector(selectMultiPaneFullScreen)

    const applyExtraSettings = useCallback(() => {
        onDrawingSettingGroupsUpdated?.(extraSettingGroups)
    }, [extraSettingGroups, onDrawingSettingGroupsUpdated])

    const handleClose = useCallback(() => {
        applyExtraSettings()
        onClose()
    }, [applyExtraSettings, onClose])

    const handleCancel = useCallback(() => {
        setExtraSettingGroups(JSON.parse(JSON.stringify(initialExtraSettingGroups.current)))
        onDrawingSettingGroupsUpdated?.(initialExtraSettingGroups.current)
        onClose()
    }, [onClose, onDrawingSettingGroupsUpdated])

    const tabs = useMemo(() => {
        return <Tabs
            value={selectedTab}
            onChange={(_event: React.SyntheticEvent, newTab: string) => {
                setSelectedTab(newTab)
            }}
            sx={{ width: '100%', overflowX: 'auto', display: 'flex' }}
        >
            {settingsGroups.map((sG) =>
                sG.visible ? <Tab key={sG.id} label={sG.displayName} value={sG.id} sx={{textTransform: 'none'}}/> : null
            )}
        </Tabs>
    }, [settingsGroups, selectedTab])

    const updateExtraSettingGroups = useCallback((sg: DrawingSettingGroup<any, any>) => {
        setExtraSettingGroups((prev) => {
            const ind = prev.findIndex((esg) => esg.id === sg.id)
            if (ind === -1) {
                return prev
            }
            const next = [...prev]
            next[ind] = { ...sg }
            return next
        })
    }, [])

    return (
        <CustomDialog
            open
            maxWidth={'xs'}
            fullWidth
            onClose={handleClose}
            container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
        >
            <DraggableDialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}>Settings</Typography>
                    <ExtraSmallButton onClick={handleClose} aria-label="close" color={'inherit'}>
                        <ClearIcon />
                    </ExtraSmallButton>
                </Stack>
            </DraggableDialogTitle>
            <DialogContent sx={{ width: '100%', padding: '16px 20px' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    {tabs}
                </Box>
                <Box sx={{display: 'grid', '& > *': {gridArea: '1 / 1'}}}>
                    {settingsGroups.map((sG) => {
                        return <Box sx={{visibility: sG.id === selectedTab ? 'visible' : 'hidden'}} key={sG.id}>
                            <ChartDrawingSettingsGroup chart={chart} id={id} group={sG} onSettingGroupUpdated={(sg) => {
                                if (extraSettingGroupIds.includes(sg.id)) {
                                    updateExtraSettingGroups(sg)
                                }
                            }} extraSettingGroup={extraSettingGroupIds.includes(sG.id)} />
                        </Box>
                    })}
                </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: 1, borderColor: 'divider', justifyContent: 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                    <Button variant="contained" onClick={handleClose}>Ok</Button>
                </Box>
            </DialogActions>
        </CustomDialog>
    )
}

export default memo(ChartDrawingSettingsModal)
